// ═══════════════════════════════════════════════════════════
// GabNotifications — Toast notification system
// Replaces Mantine's <Notifications> provider
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, createContext, useContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface Notification {
  id: string;
  message: string;
  type: NotificationType;
  autoClose: number;
  createdAt: number;
}

interface NotificationContextValue {
  show: (message: string, type?: NotificationType, autoClose?: number) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within GabNotificationProvider');
  return ctx;
}

// Also export a standalone API for use outside React components
let _globalShow: NotificationContextValue['show'] | null = null;

interface NotifyOptions { title?: string; message?: string; variant?: NotificationType; autoClose?: number; }

function _notify(opts: NotifyOptions): void;
function _notify(message: string, type?: NotificationType, autoClose?: number): void;
function _notify(arg: string | NotifyOptions, type?: NotificationType, autoClose?: number): void {
  if (typeof arg === 'string') {
    _globalShow?.(arg, type, autoClose);
  } else {
    const msg = [arg.title, arg.message].filter(Boolean).join(': ');
    _globalShow?.(msg || 'Notification', arg.variant, arg.autoClose);
  }
}

export const notify = Object.assign(_notify, {
  show: (message: string, type?: NotificationType, autoClose?: number) => _globalShow?.(message, type, autoClose),
  success: (message: string) => _globalShow?.(message, 'success'),
  error: (message: string) => _globalShow?.(message, 'error'),
  warning: (message: string) => _globalShow?.(message, 'warning'),
  info: (message: string) => _globalShow?.(message, 'info'),
});

const typeStyles: Record<NotificationType, { bg: string; border: string; color: string }> = {
  success: { bg: '#EFFDF1', border: 'var(--color-success)', color: '#065f46' },
  error: { bg: '#FCF7F7', border: 'var(--color-error)', color: 'var(--color-error)' },
  warning: { bg: '#FDF7F4', border: 'var(--color-warning)', color: 'var(--color-warning)' },
  info: { bg: '#F1FAFC', border: 'var(--color-info)', color: 'var(--color-info)' },
};

export function GabNotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const show = useCallback((message: string, type: NotificationType = 'info', autoClose = 5000) => {
    const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    setNotifications(prev => [...prev.slice(-4), { id, message, type, autoClose, createdAt: Date.now() }]);
  }, []);

  const remove = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Register global API
  useEffect(() => { _globalShow = show; return () => { _globalShow = null; }; }, [show]);

  // Auto-dismiss
  useEffect(() => {
    const timers = notifications.map(n =>
      setTimeout(() => remove(n.id), n.autoClose)
    );
    return () => timers.forEach(clearTimeout);
  }, [notifications, remove]);

  const ctx: NotificationContextValue = {
    show,
    success: (m) => show(m, 'success'),
    error: (m) => show(m, 'error'),
    warning: (m) => show(m, 'warning'),
    info: (m) => show(m, 'info'),
  };

  return (
    <NotificationContext.Provider value={ctx}>
      {children}
      {createPortal(
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 9999,
          display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 400,
        }}>
          {notifications.map(n => {
            const s = typeStyles[n.type];
            return (
              <div
                key={n.id}
                style={{
                  background: s.bg, borderLeft: `4px solid ${s.border}`, color: s.color,
                  padding: '12px 16px', borderRadius: 'var(--radius-lg)',
                  boxShadow: 'var(--shadow-lg)', fontSize: 'var(--font-size-sm)',
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  animation: 'slideInRight 200ms ease-out',
                }}
                role="alert"
              >
                <span style={{ flex: 1 }}>{n.message}</span>
                <button
                  onClick={() => remove(n.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.6, padding: 0, fontSize: 16, lineHeight: 1 }}
                >
                  &times;
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </NotificationContext.Provider>
  );
}
